import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import {
  useCompletedTasks,
  useMe,
  useProjects,
  useTags,
  useViewTasks
} from '../hooks/queries';
import { useWorkspace } from '../workspace/WorkspaceContext';
import { Avatar } from './Avatar';
import { ImportDialog } from './ImportDialog';
import { ProjectDialog } from './ProjectDialog';
import { SettingsDialog } from './SettingsDialog';
import {
  IconDone,
  IconInbox,
  IconIncoming,
  IconKnowledge,
  IconLogout,
  IconStar,
  IconCheckins,
  IconStreaks} from './icons';

interface SidebarProps {
  onNavigate: () => void;
}

export function Sidebar({ onNavigate }: SidebarProps) {
  const { t } = useTranslation();
  const { workspace } = useWorkspace();
  const { user, logout } = useAuth();
  const me = useMe();
  const [showProjectDialog, setShowProjectDialog] = useState(false);
  const [editProject, setEditProject] = useState<{
    id: string;
    name: string;
    color: string;
  } | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const showTags = me.data?.prefs?.useTags !== false;
  const showKnowledge = me.data?.prefs?.knowledgeEnabled === true;
  const showCheckins = me.data?.prefs?.checkinsEnabled === true;
  const showHabits = me.data?.prefs?.habitsEnabled === true;

  const todayTasks = useViewTasks(workspace.id, { view: 'today' });
  const incomingTasks = useViewTasks(workspace.id, { view: 'incoming' });
  const inboxTasks = useViewTasks(workspace.id, { view: 'inbox' });
  const completed = useCompletedTasks(workspace.id);
  const projects = useProjects(workspace.id);
  const tags = useTags(workspace.id);

  const completedCount = useMemo(() => {
    const pages = completed.data?.pages;
    if (!pages || pages.length === 0) return undefined;
    const total = pages.reduce((sum, page) => sum + page.items.length, 0);
    const hasMore = pages[pages.length - 1].hasMore;
    return `${total}${hasMore ? '+' : ''}`;
  }, [completed.data]);

  const sortedProjects = useMemo(
    () => (projects.data ?? [])
      .filter((p) => !p.archived)
      .sort((a, b) => a.position - b.position || a.name.localeCompare(b.name)),
    [projects.data]
  );

  const sortedTags = useMemo(
    () => [...(tags.data ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
    [tags.data]
  );

  const views = [
    {
      to: '/app/today',
      className: 'sb-view-today',
      icon: <IconStar filled className="sb-view-icon" />,
      label: t('views.today'),
      count: todayTasks.data?.length !== undefined ? String(todayTasks.data.length) : undefined
    },
    {
      to: '/app/incoming',
      className: '',
      icon: <IconIncoming className="sb-view-icon" />,
      label: t('views.incoming'),
      count:
        incomingTasks.data?.length !== undefined ? String(incomingTasks.data.length) : undefined
    },
    {
      to: '/app/inbox',
      className: '',
      icon: <IconInbox className="sb-view-icon" />,
      label: t('views.inbox'),
      count: inboxTasks.data?.length !== undefined ? String(inboxTasks.data.length) : undefined
    },
    {
      to: '/app/completed',
      className: '',
      icon: <IconDone className="sb-view-icon" />,
      label: t('views.completed'),
      count: completedCount
    }
  ];

  return (
    <aside className="sidebar">
      <div className="brand-row">
        <span className="brand-mark" />
        <span className="brand-name">{t('app.name')}</span>
      </div>
      <nav className="sb-views" aria-label={t('nav.views')}>
        {views.map((view) => (
          <NavLink
            key={view.to}
            to={view.to}
            className={({ isActive }) => `sb-view ${view.className}${isActive ? ' active' : ''}`}
            onClick={onNavigate}
          >
            {view.icon}
            <span>{view.label}</span>
            {view.count !== undefined && <span className="sb-count">{view.count}</span>}
          </NavLink>
        ))}
      </nav>

      {showCheckins && (
        <NavLink
          to="/app/checkins"
          className={({ isActive }) => `sb-view sb-knowledge${isActive ? ' active' : ''}`}
          onClick={onNavigate}
        >
          <IconCheckins className="sb-view-icon" />
          <span>{t('nav.checkins')}</span>
        </NavLink>
      )}

      {showHabits && (
        <NavLink
          to="/app/habits"
          className={({ isActive }) => `sb-view sb-knowledge${isActive ? ' active' : ''}`}
          onClick={onNavigate}
        >
          <IconStreaks className="sb-view-icon" />
          <span>{t('nav.habits')}</span>
        </NavLink>
      )}

      {showKnowledge && (
        <NavLink
          to="/app/knowledge"
          className={({ isActive }) => `sb-view sb-knowledge${isActive ? ' active' : ''}`}
          onClick={onNavigate}
        >
          <IconKnowledge className="sb-view-icon" />
          <span>{t('nav.knowledge')}</span>
        </NavLink>
      )}

      <div className="sb-section-head">
        <span className="kicker">{t('nav.projects')}</span>
        <button
          type="button"
          className="sb-add-project sb-gear"
          onClick={() => setShowSettings(true)}
          aria-label={t('nav.settings')}
          title={t('nav.settings')}
        >
          ⚙
        </button>
        <button
          type="button"
          className="sb-add-project"
          onClick={() => setShowProjectDialog(true)}
          aria-label={t('nav.addProject')}
          title={t('nav.addProject')}
        >
          +
        </button>
      </div>
      <div className="sb-projects">
        {sortedProjects.map((project) => (
          <div className="sb-project-row" key={project.id}>
            <NavLink
              to={`/app/project/${project.id}`}
              className={({ isActive }) => `sb-project${isActive ? ' active' : ''}`}
              onClick={onNavigate}
            >
              <span className="color-dot" style={{ background: project.color }} />
              <span className="sb-project-name">{project.name}</span>
              {(project.sharedWith.length > 0 || project.ownerUserId !== me.data?.id) && (
                <span className="sb-shared" title={t('nav.shared')}>⇄</span>
              )}
            </NavLink>
            {project.ownerUserId === me.data?.id && (
              <button
                type="button"
                className="sb-project-edit"
                aria-label={t('project.edit')}
                title={t('project.edit')}
                onClick={() => setEditProject(project)}
              >
                ✎
              </button>
            )}
          </div>
        ))}
      </div>

      {showTags && sortedTags.length > 0 && (
        <>
          <div className="sb-section-head">
            <span className="kicker">{t('nav.tags')}</span>
          </div>
          <div className="sb-tags">
            {sortedTags.map((tag) => (
              <NavLink
                key={tag.id}
                to={`/app/tag/${tag.id}`}
                className={({ isActive }) => `sb-tag${isActive ? ' active' : ''}`}
                onClick={onNavigate}
              >
                <span className="color-dot" style={{ background: tag.color }} />
                <span>{tag.name}</span>
              </NavLink>
            ))}
          </div>
        </>
      )}

      <div className="sb-footer-wrap">
        {userMenuOpen && (
          <div className="sb-user-menu" role="menu">
            <button
              type="button"
              onClick={() => {
                setUserMenuOpen(false);
                setShowSettings(true);
              }}
            >
              {t('nav.settings')}
            </button>
            <button
              type="button"
              onClick={() => {
                setUserMenuOpen(false);
                setShowImport(true);
              }}
            >
              {t('nav.import')}
            </button>
            <button type="button" onClick={() => void logout()}>
              <IconLogout />
              {t('nav.logout')}
            </button>
          </div>
        )}
        <button
          type="button"
          className="sb-footer"
          onClick={() => setUserMenuOpen((open) => !open)}
          aria-haspopup="menu"
          aria-expanded={userMenuOpen}
        >
          <Avatar name={user?.name || user?.email || '?'} avatarUrl={user?.avatarUrl} />
          <div className="sb-user">
            <div className="sb-user-name">{user?.name}</div>
            <div className="sb-user-email">{user?.email}</div>
          </div>
        </button>
      </div>

      {showProjectDialog && <ProjectDialog onClose={() => setShowProjectDialog(false)} />}
      {editProject && (
        <ProjectDialog project={editProject} onClose={() => setEditProject(null)} />
      )}
      {showImport && <ImportDialog onClose={() => setShowImport(false)} />}
      {showSettings && <SettingsDialog onClose={() => setShowSettings(false)} />}
    </aside>
  );
}
